import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, TrendingUp, TrendingDown, DollarSign, Users, Briefcase, BarChart3, Building2, Globe, PieChart as PieChartIcon } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import MetricInfo from "../components/MetricInfo";
import DataTable from "../components/DataTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIE_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#84CC16"];

function money(v) {
  const n = Number(v || 0);
  return `£${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function CompanyDashboard() {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [summary, setSummary] = useState(null);
  const [commercial, setCommercial] = useState(null);
  const [operations, setOperations] = useState(null);
  const [projectHealth, setProjectHealth] = useState(null);
  const [profitability, setProfitability] = useState(null);
  const [projectBudgets, setProjectBudgets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectAnalysis, setProjectAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const [employees, setEmployees] = useState([]);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const DEFAULT_EMPLOYEE = {
    display_name: "", timesheet_name: "", employment_type: "delivery", is_active: true,
    hours_per_day: "8", cost_mode: "hourly", hourly_cost: "", annual_salary: "",
    salary_conversion: "260_working_days", custom_workdays_per_year: "", currency: "GBP",
  };
  const [form, setForm] = useState(DEFAULT_EMPLOYEE);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const promises = [
        axios.get(`${API}/director/employees`),
        axios.get(`${API}/director/commercial/overview`),
        axios.get(`${API}/director/projects/health`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/profitability${dateRange.start && dateRange.end ? `?start_date=${dateRange.start}&end_date=${dateRange.end}` : ""}`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/project-budgets`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/projects${dateRange.start && dateRange.end ? `?start_date=${dateRange.start}&end_date=${dateRange.end}` : ""}`).catch(() => ({ data: [] })),
      ];
      if (dateRange.start && dateRange.end) {
        const params = `?start_date=${dateRange.start}&end_date=${dateRange.end}`;
        promises.push(axios.get(`${API}/director/company/summary${params}`));
        promises.push(axios.get(`${API}/director/operations/overview${params}`));
      } else {
        promises.push(Promise.resolve({ data: null }));
        promises.push(Promise.resolve({ data: null }));
      }
      const [empRes, commRes, healthRes, profitRes, budgetRes, projRes, sumRes, opsRes] = await Promise.all(promises);
      setEmployees(empRes.data.employees || []);
      setCommercial(commRes.data);
      setProjectHealth(healthRes.data);
      setProfitability(profitRes.data);
      setProjectBudgets(Array.isArray(budgetRes.data) ? budgetRes.data : (budgetRes.data?.items || []));
      setProjects(Array.isArray(projRes.data) ? projRes.data : (projRes.data?.items || []));
      setSummary(sumRes.data);
      setOperations(opsRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [dateRange.start, dateRange.end]);

  const openCreate = () => { setEditing(null); setForm(DEFAULT_EMPLOYEE); setEmployeeDialogOpen(true); };
  const openEdit = (emp) => {
    setEditing(emp);
    setForm({
      display_name: emp.display_name || "", timesheet_name: emp.timesheet_name || "",
      employment_type: emp.employment_type || "delivery", is_active: emp.is_active !== false,
      hours_per_day: String(emp.hours_per_day ?? "8"), cost_mode: emp.cost_mode || "hourly",
      hourly_cost: emp.hourly_cost == null ? "" : String(emp.hourly_cost),
      annual_salary: emp.annual_salary == null ? "" : String(emp.annual_salary),
      salary_conversion: emp.salary_conversion || "260_working_days",
      custom_workdays_per_year: emp.custom_workdays_per_year == null ? "" : String(emp.custom_workdays_per_year),
      currency: emp.currency || "GBP",
    });
    setEmployeeDialogOpen(true);
  };
  const saveEmployee = async () => {
    const payload = {
      display_name: form.display_name.trim(), timesheet_name: form.timesheet_name.trim() || null,
      employment_type: form.employment_type, is_active: !!form.is_active,
      hours_per_day: parseFloat(form.hours_per_day || "8"), cost_mode: form.cost_mode,
      hourly_cost: form.hourly_cost ? parseFloat(form.hourly_cost) : null,
      annual_salary: form.annual_salary ? parseFloat(form.annual_salary) : null,
      salary_conversion: form.salary_conversion,
      custom_workdays_per_year: form.custom_workdays_per_year ? parseInt(form.custom_workdays_per_year, 10) : null,
      currency: "GBP",
    };
    if (!payload.display_name) { toast.error("Display name is required"); return; }
    try {
      if (editing?.id) { await axios.put(`${API}/director/employees/${editing.id}`, payload); toast.success("Updated"); }
      else { await axios.post(`${API}/director/employees`, payload); toast.success("Added"); }
      setEmployeeDialogOpen(false); setEditing(null); setForm(DEFAULT_EMPLOYEE); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteEmployee = async (id) => {
    try { await axios.delete(`${API}/director/employees/${id}`); toast.success("Deleted"); fetchAll(); }
    catch (e) { toast.error("Failed"); }
  };

  // Chart data
  const statusChartData = (commercial?.by_status || []).filter(s => s.value > 0).map(s => ({ name: s.status, value: Math.round(s.value) }));
  const opsPersonData = (operations?.by_person || []).slice(0, 12).map(p => ({
    name: p.personnel.split(" ")[0], full: p.personnel,
    fee: Math.round(p.fee_hours), non_fee: Math.round(p.non_fee_hours),
  }));
  const opsAreaData = (operations?.by_area || []).filter(a => a.total_hours > 10).map(a => ({
    name: a.area.length > 20 ? a.area.substring(0, 18) + "..." : a.area,
    full: a.area, total: Math.round(a.total_hours), util: a.utilization,
  }));

  if (loading && !commercial) {
    return (<div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">Director</Badge>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Company Dashboard</h1>
          <p className="text-muted-foreground mt-1">P&L, Commercial, Operations & People Management</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">From</Label>
            <Input type="date" value={dateRange.start} onChange={(e) => setDateRange(p => ({ ...p, start: e.target.value }))} className="h-9 w-36" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">To</Label>
            <Input type="date" value={dateRange.end} onChange={(e) => setDateRange(p => ({ ...p, end: e.target.value }))} className="h-9 w-36" />
          </div>
          <Button variant="outline" size="sm" className="mt-5" onClick={() => setDateRange({ start: "", end: "" })} disabled={!dateRange.start && !dateRange.end}>Clear</Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap gap-2 w-full justify-start">
          <TabsTrigger value="overview" className="gap-2"><Building2 className="w-4 h-4" />Overview & P&L</TabsTrigger>
          <TabsTrigger value="commercial" className="gap-2"><DollarSign className="w-4 h-4" />Commercial</TabsTrigger>
          <TabsTrigger value="profitability" className="gap-2"><TrendingUp className="w-4 h-4" />Profitability</TabsTrigger>
          <TabsTrigger value="budgets" className="gap-2"><Briefcase className="w-4 h-4" />Budgets</TabsTrigger>
          <TabsTrigger value="operations" className="gap-2"><BarChart3 className="w-4 h-4" />Operations</TabsTrigger>
          <TabsTrigger value="projects-health" className="gap-2"><Briefcase className="w-4 h-4" />Projects Health</TabsTrigger>
          <TabsTrigger value="people" className="gap-2"><Users className="w-4 h-4" />Employees & Pay</TabsTrigger>
        </TabsList>

        {/* OVERVIEW & P&L TAB */}
        <TabsContent value="overview" className="space-y-6">
          {summary ? (
            <>
              {/* Revenue Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="rounded-xl border border-border/80 shadow-[0_1px_0_rgba(16,24,40,0.04)]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold inline-flex items-center gap-1">Revenue (All Projects) <MetricInfo text="Sum of Project Value GBP across all master projects" /></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold tabular-nums">{money(summary.revenue?.total_master_revenue)}</p>
                  </CardContent>
                </Card>
                <Card className="rounded-xl border border-border/80 shadow-[0_1px_0_rgba(16,24,40,0.04)]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold inline-flex items-center gap-1">Revenue (Active) <MetricInfo text="Only projects with timesheet activity in range" /></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold tabular-nums">{money(summary.revenue?.active_revenue_in_range)}</p>
                  </CardContent>
                </Card>
                <Card className="rounded-xl border border-border/80 shadow-[0_1px_0_rgba(16,24,40,0.04)]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold inline-flex items-center gap-1">Total Cost <MetricInfo text="Delivery + Overhead + Idle cost" /></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold tabular-nums">{money(summary.costs?.total_cost)}</p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      <Badge variant="secondary" className="gap-1 text-xs">Delivery: {money(summary.costs?.delivery_cost)}</Badge>
                      <Badge variant="secondary" className="gap-1 text-xs">Overhead: {money(summary.costs?.overhead_cost)}</Badge>
                      <Badge variant="secondary" className="gap-1 text-xs">Idle: {money(summary.costs?.idle_cost)}</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Profit Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className={`rounded-xl border ${Number(summary.profit?.net_profit_active || 0) >= 0 ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">Net Profit (Active Revenue)</CardTitle>
                    <CardDescription>Active revenue minus total cost</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className={`text-4xl font-bold tabular-nums ${Number(summary.profit?.net_profit_active || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{money(summary.profit?.net_profit_active)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {Number(summary.profit?.net_margin_active || 0) >= 0 ? <TrendingUp className="w-4 h-4 text-emerald-500" /> : <TrendingDown className="w-4 h-4 text-red-500" />}
                      <span className="text-sm font-medium">{Number(summary.profit?.net_margin_active || 0).toFixed(1)}% margin</span>
                    </div>
                  </CardContent>
                </Card>
                <Card className={`rounded-xl border ${Number(summary.profit?.net_profit_all || 0) >= 0 ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">Net Profit (All Revenue)</CardTitle>
                    <CardDescription>All master revenue minus total cost</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className={`text-4xl font-bold tabular-nums ${Number(summary.profit?.net_profit_all || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{money(summary.profit?.net_profit_all)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {Number(summary.profit?.net_margin_all || 0) >= 0 ? <TrendingUp className="w-4 h-4 text-emerald-500" /> : <TrendingDown className="w-4 h-4 text-red-500" />}
                      <span className="text-sm font-medium">{Number(summary.profit?.net_margin_all || 0).toFixed(1)}% margin</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Capacity & Utilization */}
              <Card>
                <CardHeader><CardTitle className="text-base font-semibold">Capacity & Utilization</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {[
                      { label: "Capacity (Total)", value: summary.hours?.capacity_hours_total, info: "Weekdays x hours/day for all employees" },
                      { label: "Logged (Employees)", value: summary.hours?.logged_hours_total, info: "Timesheet hours matched to employees" },
                      { label: "Billable", value: summary.hours?.billable_hours_total, color: "text-emerald-600" },
                      { label: "Non-Billable", value: summary.hours?.non_billable_hours_total, color: "text-amber-600" },
                      { label: "Idle/Unaccounted", value: summary.hours?.idle_hours_total, color: Number(summary.hours?.idle_hours_total || 0) < 0 ? "text-amber-600" : "" },
                      { label: "Unmapped Hours", value: summary.hours?.unmapped_hours_total, info: "People not in Employees & Pay" },
                    ].map((item, i) => (
                      <div key={i} className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground inline-flex items-center gap-1">{item.label} {item.info && <MetricInfo text={item.info} />}</p>
                        <p className={`text-xl font-bold tabular-nums ${item.color || ""}`}>{item.value ?? 0}h</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Per-Employee Table */}
              {(summary.per_employee || []).length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base font-semibold">Per Employee Breakdown</CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-muted-foreground">
                            <th className="text-left py-2 pr-3 font-medium">Employee</th>
                            <th className="text-left py-2 pr-3 font-medium">Type</th>
                            <th className="text-right py-2 px-3 font-medium">Capacity</th>
                            <th className="text-right py-2 px-3 font-medium">Logged</th>
                            <th className="text-right py-2 px-3 font-medium">Idle</th>
                            <th className="text-right py-2 px-3 font-medium">Hourly Cost</th>
                            <th className="text-right py-2 px-3 font-medium">Logged Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.per_employee.map(r => (
                            <tr key={r.timesheet_name} className="border-b border-border/50">
                              <td className="py-2 pr-3 font-medium">{r.display_name}</td>
                              <td className="py-2 pr-3"><Badge variant="secondary" className="text-xs">{r.employment_type}</Badge></td>
                              <td className="py-2 px-3 text-right tabular-nums">{r.capacity_hours}h</td>
                              <td className="py-2 px-3 text-right tabular-nums">{r.logged_hours}h</td>
                              <td className={`py-2 px-3 text-right tabular-nums ${Number(r.idle_hours || 0) < 0 ? "text-amber-600" : ""}`}>{r.idle_hours}h</td>
                              <td className="py-2 px-3 text-right tabular-nums">£{Number(r.hourly_cost || 0).toFixed(2)}</td>
                              <td className="py-2 px-3 text-right tabular-nums">{money(r.cost_logged)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </TabsContent>

        {/* COMMERCIAL TAB */}
        <TabsContent value="commercial" className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="rounded-xl border border-border/80"><CardContent className="pt-6">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Total Projects</p>
              <p className="text-3xl font-bold tabular-nums">{commercial?.total_projects || 0}</p>
            </CardContent></Card>
            <Card className="rounded-xl border border-border/80"><CardContent className="pt-6">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Total Portfolio Value</p>
              <p className="text-3xl font-bold tabular-nums">{money(commercial?.total_value)}</p>
            </CardContent></Card>
            <Card className="rounded-xl border border-border/80"><CardContent className="pt-6">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Pipeline Value</p>
              <p className="text-3xl font-bold text-primary tabular-nums">{money(commercial?.pipeline_value)}</p>
              <p className="text-xs text-muted-foreground mt-1">Pricing + Work Started</p>
            </CardContent></Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-base font-semibold">Revenue by Status</CardTitle></CardHeader>
              <CardContent>
                {statusChartData.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={statusChartData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                          {statusChartData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => [`£${v.toLocaleString()}`, ""]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : <p className="text-center text-muted-foreground py-8">No data</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base font-semibold">Status Breakdown</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(commercial?.by_status || []).map(s => (
                    <div key={s.status} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div>
                        <p className="font-medium">{s.status}</p>
                        <p className="text-xs text-muted-foreground">{s.count} projects</p>
                      </div>
                      <p className="font-semibold tabular-nums">{money(s.value)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {(commercial?.by_owner || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base font-semibold">Commercial Owners</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border"><th className="text-left py-2 px-3">Owner</th><th className="text-right py-2 px-3">Projects</th><th className="text-right py-2 px-3">Value</th></tr></thead>
                    <tbody>
                      {commercial.by_owner.map(o => (
                        <tr key={o.owner} className="border-b border-border/50"><td className="py-2 px-3 font-medium">{o.owner}</td><td className="py-2 px-3 text-right tabular-nums">{o.projects}</td><td className="py-2 px-3 text-right tabular-nums font-medium">{money(o.value)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* PROFITABILITY TAB */}
        <TabsContent value="profitability" className="space-y-6">
          {profitability ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="rounded-xl border border-border/80"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Total Revenue</p>
                  <p className="text-3xl font-bold tabular-nums">{money(profitability.summary?.project_revenue_total)}</p>
                  <p className="text-xs text-muted-foreground mt-1">From Project Master values</p>
                </CardContent></Card>
                <Card className="rounded-xl border border-border/80"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Billable Hours</p>
                  <p className="text-3xl font-bold tabular-nums">{profitability.summary?.total_billable_hours || 0}h</p>
                  <p className="text-xs text-muted-foreground mt-1">of {profitability.summary?.total_hours || 0}h total</p>
                </CardContent></Card>
                <Card className="rounded-xl border border-border/80"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Utilization</p>
                  <p className="text-3xl font-bold tabular-nums">{profitability.summary?.utilization_rate || 0}%</p>
                  <Progress value={profitability.summary?.utilization_rate || 0} className="mt-2 h-2" />
                </CardContent></Card>
                <Card className="rounded-xl border border-border/80"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Profit Margin</p>
                  <p className="text-3xl font-bold tabular-nums">{profitability.projects_overall?.profit_margin || 0}%</p>
                </CardContent></Card>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="rounded-xl"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Total Profit</p>
                  <p className={`text-2xl font-bold tabular-nums ${(profitability.projects_overall?.total_profit || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{money(profitability.projects_overall?.total_profit)}</p>
                </CardContent></Card>
                <Card className="rounded-xl"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Cost Leakage</p>
                  <p className="text-2xl font-bold text-amber-600 tabular-nums">{money(profitability.projects_overall?.total_leakage)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Non-chargeable hours cost</p>
                </CardContent></Card>
                <Card className="rounded-xl"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Avg Hourly Rate</p>
                  <p className="text-2xl font-bold tabular-nums">{money(profitability.summary?.average_hourly_rate)}/hr</p>
                </CardContent></Card>
              </div>

              {/* Projects profitability table */}
              {(profitability.by_project || []).length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base font-semibold">Project Profitability</CardTitle><CardDescription>Click a project for detailed analysis</CardDescription></CardHeader>
                  <CardContent>
                    <DataTable
                      data={profitability.by_project || []}
                      searchFields={["project_id", "status", "client_name"]}
                      columns={[
                        { key: "project_id", label: "Project", cellClassName: "font-medium truncate max-w-[200px]" },
                        { key: "status", label: "Status", render: (r) => <Badge variant="secondary" className="text-xs">{r.status || "N/A"}</Badge> },
                        { key: "revenue", label: "Revenue", cellClassName: "text-right tabular-nums", render: (r) => money(r.revenue) },
                        { key: "actual_cost", label: "Cost", cellClassName: "text-right tabular-nums", render: (r) => money(r.actual_cost) },
                        { key: "profit", label: "Profit", render: (r) => <span className={`tabular-nums font-medium ${(r.profit || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{money(r.profit)}</span> },
                        { key: "profit_margin", label: "Margin", cellClassName: "text-right tabular-nums", render: (r) => `${(r.profit_margin || 0).toFixed(1)}%` },
                      ]}
                      defaultSort="revenue"
                      searchPlaceholder="Search projects..."
                      onRowClick={async (p) => {
                        try {
                          const res = await axios.get(`${API}/admin/project-analysis/${encodeURIComponent(p.project_id)}`);
                          setProjectAnalysis(res.data);
                          setSelectedProject(p.project_id);
                        } catch {}
                      }}
                      emptyMessage="No project data"
                    />
                  </CardContent>
                </Card>
              )}

              {/* Consultant revenue */}
              {(profitability.by_consultant || []).length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base font-semibold">Revenue by Consultant</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {profitability.by_consultant.map(c => (
                        <div key={c.personnel} className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/30 transition-[background-color] duration-150">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-semibold text-primary">{c.personnel.split(" ").map(n => n[0]).join("")}</span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium">{c.personnel}</span>
                              <span className="text-sm font-semibold">{money(c.revenue)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Progress value={(c.billable_hours / (c.total_hours || 1)) * 100} className="h-2 flex-1" />
                              <span className="text-xs text-muted-foreground">{c.billable_hours}h / {c.total_hours}h</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-dashed"><CardContent className="flex flex-col items-center justify-center py-16">
              <TrendingUp className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Loading profitability data...</h3>
            </CardContent></Card>
          )}
        </TabsContent>

        {/* BUDGETS TAB */}
        <TabsContent value="budgets" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Project Budgets</CardTitle>
              <CardDescription>Set and track budget hours and amounts for projects</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Select value={selectedProject || ""} onValueChange={setSelectedProject}>
                  <SelectTrigger><SelectValue placeholder="Select project to set budget" /></SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>{p.project_id} ({p.company})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Input id="budget-hours" type="number" step="0.01" min="0" placeholder="Budget hours" />
                  <Input id="budget-amount" type="number" step="0.01" min="0" placeholder="Budget £" />
                </div>
                <Button className="w-full gap-2" onClick={async () => {
                  const hoursEl = document.getElementById("budget-hours");
                  const amountEl = document.getElementById("budget-amount");
                  if (!selectedProject || !hoursEl?.value) { toast.error("Select project and enter hours"); return; }
                  try {
                    await axios.post(`${API}/admin/project-budgets`, {
                      project_id: selectedProject,
                      budget_hours: parseFloat(hoursEl.value),
                      budget_amount: parseFloat(amountEl?.value || 0),
                      currency: "GBP",
                    });
                    toast.success("Budget saved");
                    hoursEl.value = ""; if (amountEl) amountEl.value = "";
                    fetchAll();
                  } catch (e) { toast.error("Failed to save budget"); }
                }}><Plus className="w-4 h-4" /> Set Budget</Button>
              </div>
              <div className="space-y-2">
                {projectBudgets.map(b => (
                  <div key={b.project_id} className="p-3 rounded-lg bg-muted/50 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{b.project_id}</p>
                      <p className="text-sm text-muted-foreground">{b.budget_hours}h budget {b.budget_amount > 0 ? `· ${money(b.budget_amount)}` : ""}</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={async () => {
                      try { await axios.delete(`${API}/admin/project-budgets/${encodeURIComponent(b.project_id)}`); toast.success("Deleted"); fetchAll(); }
                      catch { toast.error("Failed"); }
                    }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ))}
                {projectBudgets.length === 0 && <p className="text-center text-muted-foreground py-4">No budgets set yet</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* OPERATIONS TAB */}
        <TabsContent value="operations" className="space-y-6">
          {operations ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Card className="rounded-xl border border-border/80"><CardContent className="pt-6"><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Utilization Rate</p><p className="text-3xl font-bold tabular-nums">{operations.utilization_rate}%</p><Progress value={operations.utilization_rate} className="mt-2 h-2" /></CardContent></Card>
                <Card className="rounded-xl border border-border/80"><CardContent className="pt-6"><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Total Hours</p><p className="text-3xl font-bold tabular-nums">{operations.total_hours}h</p></CardContent></Card>
                <Card className="rounded-xl border border-border/80"><CardContent className="pt-6"><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Billable</p><p className="text-3xl font-bold text-emerald-600 tabular-nums">{operations.fee_hours}h</p></CardContent></Card>
                <Card className="rounded-xl border border-border/80"><CardContent className="pt-6"><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Personnel</p><p className="text-3xl font-bold tabular-nums">{operations.personnel_count}</p></CardContent></Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader><CardTitle className="text-base font-semibold">Personnel Utilization</CardTitle></CardHeader>
                  <CardContent>
                    {opsPersonData.length > 0 ? (
                      <div className="h-[350px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={opsPersonData} layout="vertical" margin={{ left: 10, right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal vertical={false} />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={70} tick={{ fontSize: 11 }} />
                            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                            <Legend />
                            <Bar dataKey="fee" name="Billable" fill="#10B981" radius={[0, 4, 4, 0]} />
                            <Bar dataKey="non_fee" name="Non-Billable" fill="#F59E0B" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : <p className="text-center text-muted-foreground py-8">No data</p>}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-base font-semibold">Hours by Category/Area</CardTitle></CardHeader>
                  <CardContent>
                    {opsAreaData.length > 0 ? (
                      <div className="h-[350px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={opsAreaData} margin={{ left: 10, right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                            <YAxis />
                            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                            <Bar dataKey="total" name="Total Hours" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : <p className="text-center text-muted-foreground py-8">No data</p>}
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Personnel Table */}
              <Card>
                <CardHeader><CardTitle className="text-base font-semibold">Personnel Detail</CardTitle></CardHeader>
                <CardContent>
                  <DataTable
                    data={operations.by_person || []}
                    searchFields={["personnel"]}
                    columns={[
                      { key: "personnel", label: "Personnel", cellClassName: "font-medium" },
                      { key: "total_hours", label: "Total", cellClassName: "text-right tabular-nums", render: (r) => `${r.total_hours}h` },
                      { key: "fee_hours", label: "Billable", cellClassName: "text-right tabular-nums text-emerald-600", render: (r) => `${r.fee_hours}h` },
                      { key: "non_fee_hours", label: "Non-Billable", cellClassName: "text-right tabular-nums text-amber-600", render: (r) => `${r.non_fee_hours}h` },
                      { key: "utilization", label: "Utilization", render: (r) => (
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full" style={{ width: `${r.utilization}%`, backgroundColor: r.utilization >= 50 ? '#10B981' : '#F59E0B' }} /></div>
                          <span className="tabular-nums text-xs">{r.utilization}%</span>
                        </div>
                      )},
                    ]}
                    defaultSort="total_hours"
                    searchPlaceholder="Search personnel..."
                    emptyMessage="No personnel data"
                  />
                </CardContent>
              </Card>
            </>
          ) : null}
        </TabsContent>

        {/* PROJECTS HEALTH TAB */}
        <TabsContent value="projects-health" className="space-y-6">
          {projectHealth ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Card className="rounded-xl border border-border/80"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Active Projects</p>
                  <p className="text-3xl font-bold tabular-nums">{projectHealth.total_active_projects}</p>
                </CardContent></Card>
                <Card className="rounded-xl border border-emerald-200 bg-emerald-50/50"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-emerald-600 mb-1">Healthy</p>
                  <p className="text-3xl font-bold text-emerald-600 tabular-nums">{projectHealth.healthy_count}</p>
                  <p className="text-xs text-emerald-500">Score 80+</p>
                </CardContent></Card>
                <Card className="rounded-xl border border-red-200 bg-red-50/50"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-red-600 mb-1">At Risk</p>
                  <p className="text-3xl font-bold text-red-600 tabular-nums">{projectHealth.at_risk_count}</p>
                  <p className="text-xs text-red-500">Score below 60</p>
                </CardContent></Card>
                <Card className="rounded-xl border border-border/80"><CardContent className="pt-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Avg Health Score</p>
                  <p className="text-3xl font-bold tabular-nums">{projectHealth.average_health}/100</p>
                </CardContent></Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-semibold">Project Health Dashboard</CardTitle>
                  <CardDescription>Active projects sorted by health score (lowest first = needs attention)</CardDescription>
                </CardHeader>
                <CardContent>
                  <DataTable
                    data={projectHealth.projects || []}
                    searchFields={["project_id", "company", "status", "business_area"]}
                    columns={[
                      { key: "health_score", label: "Health", render: (p) => (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                          p.health_score >= 80 ? "bg-emerald-500" : p.health_score >= 60 ? "bg-amber-500" : "bg-red-500"
                        }`}>{p.health_score}</div>
                      )},
                      { key: "project_id", label: "Project", render: (p) => (<div><p className="font-medium truncate max-w-[200px]">{p.project_id}</p><p className="text-xs text-muted-foreground">{p.company} · {p.business_area}</p></div>) },
                      { key: "status", label: "Status", render: (p) => <Badge variant="secondary" className="text-xs">{p.status}</Badge> },
                      { key: "project_value", label: "Value", cellClassName: "text-right tabular-nums", render: (p) => money(p.project_value) },
                      { key: "total_quoted", label: "Quoted", cellClassName: "text-right tabular-nums", render: (p) => p.total_quoted > 0 ? `${p.total_quoted}h` : "-" },
                      { key: "total_actual", label: "Actual", cellClassName: "text-right tabular-nums font-medium", render: (p) => `${p.total_actual}h` },
                      { key: "utilization", label: "Util %", cellClassName: "text-right tabular-nums", render: (p) => `${p.utilization}%` },
                      { key: "risk_flags", label: "Risk Flags", sortable: false, render: (p) => (
                        <div className="flex flex-wrap gap-1">
                          {(p.risk_flags || []).map((flag, i) => <Badge key={i} variant="destructive" className="text-xs">{flag}</Badge>)}
                          {(p.risk_flags || []).length === 0 && <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-800">Healthy</Badge>}
                        </div>
                      )},
                    ]}
                    defaultSort="health_score"
                    defaultOrder="asc"
                    searchPlaceholder="Search projects..."
                    emptyMessage="No project health data"
                  />
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-dashed"><CardContent className="flex flex-col items-center justify-center py-16"><Briefcase className="w-12 h-12 text-muted-foreground mb-4" /><h3 className="text-lg font-semibold mb-2">Loading project health...</h3></CardContent></Card>
          )}
        </TabsContent>

        {/* PEOPLE TAB */}
        <TabsContent value="people" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-base font-semibold">Employees & Pay</CardTitle>
                  <CardDescription>Manage employee roster, cost rates, and capacity (Director-only: salary data hidden from admins)</CardDescription>
                </div>
                <Button className="gap-2" onClick={openCreate}><Plus className="w-4 h-4" /> Add Employee</Button>
              </div>
            </CardHeader>
            <CardContent>
              <DataTable
                data={employees}
                searchFields={["display_name", "timesheet_name", "employment_type"]}
                columns={[
                  { key: "display_name", label: "Name", render: (e) => <span className="font-medium">{e.display_name} {!e.is_active && <Badge variant="destructive" className="ml-1 text-xs">Inactive</Badge>}</span> },
                  { key: "timesheet_name", label: "Timesheet Name", cellClassName: "text-muted-foreground", render: (e) => e.timesheet_name || e.display_name },
                  { key: "employment_type", label: "Type", render: (e) => <Badge variant="secondary" className="text-xs">{e.employment_type}</Badge> },
                  { key: "cost_mode", label: "Cost Mode" },
                  { key: "hourly_cost", label: "Rate/Salary", cellClassName: "text-right tabular-nums", render: (e) => e.cost_mode === "hourly" ? `£${e.hourly_cost || 0}/hr` : `£${Number(e.annual_salary || 0).toLocaleString()}/yr` },
                  { key: "hours_per_day", label: "Hours/Day", cellClassName: "text-right tabular-nums", render: (e) => `${e.hours_per_day}h` },
                  { key: "_actions", label: "", sortable: false, render: (e) => (
                    <div className="flex gap-1">
                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={(ev) => { ev.stopPropagation(); openEdit(e); }}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(ev) => { ev.stopPropagation(); deleteEmployee(e.id); }}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                    </div>
                  )},
                ]}
                defaultSort="display_name"
                defaultOrder="asc"
                searchPlaceholder="Search employees..."
                emptyMessage="No employees added. Add employees to calculate capacity and costs."
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Employee Dialog */}
      <Dialog open={employeeDialogOpen} onOpenChange={setEmployeeDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Employee" : "Add Employee"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Display Name</Label><Input value={form.display_name} onChange={(e) => setForm(p => ({ ...p, display_name: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Timesheet Name</Label><Input value={form.timesheet_name} onChange={(e) => setForm(p => ({ ...p, timesheet_name: e.target.value }))} placeholder="Must match timesheet personnel exactly" /><p className="text-xs text-muted-foreground">Connects employee to timesheet rows</p></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Type</Label><Select value={form.employment_type} onValueChange={(v) => setForm(p => ({ ...p, employment_type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="delivery">Delivery</SelectItem><SelectItem value="overhead">Overhead</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label>Hours/Day</Label><Input type="number" step="0.01" min="0" value={form.hours_per_day} onChange={(e) => setForm(p => ({ ...p, hours_per_day: e.target.value }))} /></div>
            </div>
            <div className="space-y-2"><Label>Cost Mode</Label><Select value={form.cost_mode} onValueChange={(v) => setForm(p => ({ ...p, cost_mode: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="hourly">Hourly Cost</SelectItem><SelectItem value="salary">Annual Salary</SelectItem></SelectContent></Select></div>
            {form.cost_mode === "hourly" ? (
              <div className="space-y-2"><Label>Hourly Cost (£/hr)</Label><Input type="number" step="0.01" min="0" value={form.hourly_cost} onChange={(e) => setForm(p => ({ ...p, hourly_cost: e.target.value }))} /></div>
            ) : (
              <div className="space-y-2"><Label>Annual Salary (£/yr)</Label><Input type="number" step="0.01" min="0" value={form.annual_salary} onChange={(e) => setForm(p => ({ ...p, annual_salary: e.target.value }))} /></div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEmployeeDialogOpen(false)}>Cancel</Button>
              <Button onClick={saveEmployee}>{editing ? "Save" : "Add"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
